import {
  ProductsCompareExport,
  ProductsCompareKpi,
  ProductsParam,
  RetailerKpiOption,
  SaleType,
  TrendsKpi,
} from '@models/Products';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { useExportTable } from '@hooks/export/export-table/UseExportTable';
import { useToastMessage } from '../toast-message/ToastMessage';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { FactRevenue, Option, PageName, PageType } from '@utils/pageConfig/Types';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import getComparisonPeriod, { PeriodFormat } from '../filter-selection-note/GetComparisonPeriod';
import {
  displayExportErrorToastMessage,
  getAppliedFiltersText,
  getKpiDisplayNameFromHeaderKey,
} from '@hooks/export/ExportUtils';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import { RI_PRODUCT_LEADERBOARD_COMPARE_PRODUCTS_TITLE } from '@pages/Utilities';
import { RelativePeriod } from '../comparison-period-selector/Types';
import { capitalize } from 'lodash';
import { formatProductsPeriod, isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { useExportMultiChart } from '@hooks/export/utilities/UseExportMultiChart';
import { MARKET, RETAILER } from './Types';
import { SaleTypeMappingProducts } from '@const/PerformanceType';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { ChartKeys } from '@const/ChartKeys';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import {
  processProductsMarketLineChartData,
  processProductsRetailerLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';
import { useCallback } from 'react';

export interface UseProductsCompareExportExcelParam {
  productsParam?: ProductsParam;
  mainFilterStateProducts: any;
  productsCompareExport: ProductsCompareExport[];
  pagesConfig: PagesConfig;
  appliedCountries: Option[];
  appliedPg?: Option[];
  factRevenue: FactRevenue;
  timeFrame: Periodicity;
  leaderboardUpdate: boolean;
  selectedSalesType: SaleType;
  selectedRetailerOption: RetailerKpiOption;
  featureData: ProductsCompareKpi;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  latestPeriod: BaseTimePeriod;
  trendsKpi: TrendsKpi;
}

export const useProductsCompareExport = (
  param: UseProductsCompareExportExcelParam,
): ((exportFormats: ExportFormat[]) => Promise<void>) => {
  const {
    productsParam,
    productsCompareExport,
    pagesConfig,
    appliedCountries,
    appliedPg,
    factRevenue,
    timeFrame,
    leaderboardUpdate,
    selectedSalesType,
    selectedRetailerOption,
    featureData,
    comparisonPeriod,
    latestPeriod,
    trendsKpi,
  } = param;
  const { exportImages } = useExportImages(PageType.RI_PRODUCT);
  const { download: exportTable } = useExportTable([]);
  const { download: exportLineChart } = useExportMultiChart();
  const { showToastMessage } = useToastMessage();
  //Amplitude tracking
  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersProducts', 'hitlistsRetailerClient');

  const handleExportedTracking = (exportFormats: ExportFormat[]) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType: ElementType.LINE_CHART,
      elementKpi: capitalize(productsParam?.kpi),
      elementSort: pagesConfig.sortingOptionsProducts,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  return useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!productsParam || productsCompareExport.length < 1) {
        return;
      }

      const marketChartData = processProductsMarketLineChartData(trendsKpi, selectedSalesType);
      const retailerChartData = processProductsRetailerLineChartData(
        trendsKpi,
        selectedSalesType,
        selectedRetailerOption,
      );

      const exportImage = [
        exportImages[generateUniqueChartKeys(marketChartData, ChartKeys[PageType.RI_PRODUCT].MARKET)],
        exportImages[generateUniqueChartKeys(retailerChartData, ChartKeys[PageType.RI_PRODUCT].RETAILER)],
      ];

      const comparisonTimePeriod = getComparisonPeriod(
        {
          label: productsParam.period,
          value: productsParam.period,
          lastUpdatedDate: 0,
        },
        productsParam.periodType,
        comparisonPeriod,
        PeriodFormat.LONG,
      );

      try {
        const selectedKpiOption: Option = {
          value: pagesConfig.sortingOptions.headerKey,
          label: getKpiDisplayNameFromHeaderKey(pagesConfig.sortingOptions.headerKey),
        };

        if (exportFormats.includes(ExportFormat.Xlsx)) {
          await exportTable({
            exportFormats,
            title: RI_PRODUCT_LEADERBOARD_COMPARE_PRODUCTS_TITLE,
            appliedCountry: appliedCountries,
            appliedPg: appliedPg,
            data: productsCompareExport,
            exportImage,
            timePeriod: formatProductsPeriod(productsParam.period, productsParam.periodType, true),
            comparisonTimePeriod: formatProductsPeriod(comparisonTimePeriod, productsParam.periodType, true),
            factRevenue,
            periodType: productsParam.periodType,
            showPerformanceWithin: '',
            appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.RI_PRODUCT),
            selectedKpi: selectedKpiOption,
            pageName: PageName.RI_PRODUCT_COMPARE,
            pagesConfig: pagesConfig,
          });
        }

        const kpiTitle = capitalize(SaleTypeMappingProducts[selectedSalesType]);
        const marketTitle = MARKET.concat(kpiTitle);
        const retailerTitle =
          selectedSalesType === SaleType.price
            ? `${RETAILER}${kpiTitle}`
            : `${RETAILER}${kpiTitle} ${capitalize(selectedRetailerOption)}`;

        const lineChartFocusPeriod = isPeriodYTDOrMAT({
          label: productsParam.period,
          value: productsParam.period,
          lastUpdatedDate: 0,
        })
          ? latestPeriod.label
          : productsParam.period;

        const lineChartComparisonTimePeriod = getComparisonPeriod(
          {
            label: lineChartFocusPeriod,
            value: lineChartFocusPeriod,
            lastUpdatedDate: 0,
          },
          productsParam.periodType,
          comparisonPeriod,
          PeriodFormat.LONG,
        );

        await exportLineChart({
          title: RI_PRODUCT_LEADERBOARD_COMPARE_PRODUCTS_TITLE,
          appliedCountry: appliedCountries,
          appliedPg: appliedPg,
          data: featureData,
          exportImage,
          timePeriod: formatProductsPeriod(lineChartFocusPeriod, productsParam.periodType, true),
          comparisonTimePeriod: formatProductsPeriod(lineChartComparisonTimePeriod, productsParam.periodType, true),
          factRevenue,
          periodType: productsParam.periodType,
          showPerformanceWithin: '',
          appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.RI_PRODUCT),
          selectedKpi: selectedKpiOption,
          chartTitles: ['Compare Models', 'Compare Models'],
          chartSubTitles: [marketTitle, retailerTitle],
          pageName: PageName.RI_PRODUCT_COMPARE,
          exportFormats: exportFormats,
        });

        handleExportedTracking(exportFormats);
      } catch (e: any) {
        displayExportErrorToastMessage(e, 'Products Compare Modals - export error.', productsParam, showToastMessage);
        return;
      }
    },
    [productsParam, productsCompareExport, timeFrame, leaderboardUpdate, factRevenue, exportImages],
  );
};
